import React from 'react';

import { Grid } from '@ha/dsl';
import { getToday, convertDateToUTC } from '@ha/date';

import {
  DateRangePickerProps,
  DateSelectionRules,
  FOCUSED_FIELD,
  DayPickerProps,
  DATEPICKER_VARIANT,
} from '../types';
import { isDayInMoveInWindow } from '../helpers/isDayInMoveInWindow';
import { isDayAfter } from '../helpers/isDayAfter';
import { isDayBefore } from '../helpers/isDayBefore';
import { firstAvailableDay } from '../helpers/firstAvailableDay';
import { calcFirstAvailableToDate } from '../helpers/calcFirstAvailableToDate';
import { isFromDateDisabled } from '../helpers/isFromDateDisabled';
import { isToDateDisabled } from '../helpers/isToDateDisabled';
import { setDateTimeToNoon } from '../helpers/setDateTimeToNoon';
import { DateRangePickerOverlay } from './DateRangePickerOverlay';
import { DateRangePickerInputs } from './DateRangePickerInputs';
import { DatePickerDialog, DatePickerPopover } from '../Overlays';

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  AdditionalInfo = null,
  autoClose = true,
  noEndDate = false,
  noEndDateLabel,
  CaptionSubtitle,
  DatePickerInputs = DateRangePickerInputs,
  value: { from, to },
  variant = DATEPICKER_VARIANT.INLINE,
  clearDatesButton,
  fromInputLabel,
  toInputLabel,
  fromEmptyValueLabel,
  toEmptyValueLabel,
  exclusions = [],
  datePickerProps = {},
  initialValidation = false,
  fromInputProps = {},
  toInputProps = {},
  onDateChange,
  onBlur,
  minimumPeriod = 0,
  moveInWindows = [],
  moveInWindowsTooltip,
  overlayDoneBtnLabel,
  buttonProps = {},
  required = false,
  minDate = getToday(),
  maxDate = null,
  header = null,
  showSingleMonth = false,
  T,
  localeString,
  listingFlexibleDays,
  formatDate,
  withoutDividers = false,
  dialogTitle,
  onOverlayDone,
  onOverlayCancel,
  fullScreenDialog,
  showCancelButton,
  maximumStay = 0,
}) => {
  const [enteredTo, setEnteredTo] = React.useState<Date | undefined>(to);
  const [focused, setFocused] = React.useState<FOCUSED_FIELD | null>(null);
  const [fromDatePickerMonth, setFromDatePickerMonth] = React.useState<Date>();
  const [toDatePickerMonth, setToDatePickerMonth] = React.useState<Date>();

  const today = getToday();

  const dateSelectionRules: DateSelectionRules = {
    minDate,
    maxDate,
  };

  React.useEffect(() => {
    if (from && initialValidation) {
      let isFromDateInvalid = isFromDateDisabled(
        from,
        exclusions,
        minimumPeriod,
        dateSelectionRules,
      );

      if (!isFromDateInvalid && moveInWindows.length > 0) {
        isFromDateInvalid = !isDayInMoveInWindow(moveInWindows, from);
      }
      let isToDateInvalid = false;

      if (to) {
        isToDateInvalid = isToDateDisabled(
          listingFlexibleDays,
          to,
          from,
          exclusions,
          minimumPeriod,
          maximumStay,
        );
      }

      if (isFromDateInvalid || isToDateInvalid) {
        onDateChange({
          from: undefined,
          to: undefined,
        });
      }
    }
  }, []);

  React.useEffect(() => {
    // reset selected range if to becomes undefined (initial validation)
    if (!to && enteredTo) {
      setEnteredTo(undefined);
    }
  }, [to]);

  // whether a "From" date is disabled
  const isFromDisabled = (day: Date) =>
    isFromDateDisabled(day, exclusions, minimumPeriod, dateSelectionRules);
  // whether a "To" date is disabled
  const isToDisabled = (day: Date) =>
    isToDateDisabled(
      listingFlexibleDays,
      day,
      from,
      exclusions,
      minimumPeriod,
      maximumStay,
      dateSelectionRules,
    );

  // From/To change handlers
  const onFromDateChange = React.useCallback(
    (date: Date) => {
      setFocused(FOCUSED_FIELD.TO);

      if (to) {
        if (isDayAfter(date, to)) {
          onDateChange({
            from: setDateTimeToNoon(date),
            to: undefined,
          });

          setEnteredTo(undefined);

          return;
        }

        if (minimumPeriod) {
          const isDateDisabled = isToDateDisabled(
            listingFlexibleDays,
            to,
            date,
            exclusions,
            minimumPeriod,
            maximumStay,
          );

          if (isDateDisabled) {
            onDateChange({
              from: setDateTimeToNoon(date),
              to: undefined,
            });

            setEnteredTo(undefined);

            return;
          }
        }
      }

      onDateChange({
        from: setDateTimeToNoon(date),
        to,
      });
    },
    [
      to,
      exclusions,
      listingFlexibleDays,
      minimumPeriod,
      onDateChange,
      maximumStay,
    ],
  );

  const onToDateChange = React.useCallback(
    (date: Date) => {
      if (from && isDayBefore(date, from)) {
        onDateChange({
          from: setDateTimeToNoon(date),
          to: undefined,
        });

        setEnteredTo(undefined);
        setFocused(FOCUSED_FIELD.TO);

        return;
      }

      onDateChange({
        from,
        to: setDateTimeToNoon(date),
      });
      setEnteredTo(setDateTimeToNoon(date));

      setFocused(null);
    },
    [from, onDateChange],
  );

  const onMouseEnter = React.useCallback(
    (day: Date) => {
      if (!from) {
        return;
      }
      const isRangeSelected = from && to;

      if (isRangeSelected) {
        return;
      }
      if (isDayBefore(day, from)) {
        setEnteredTo(undefined);

        return;
      }
      if (!to) {
        setEnteredTo(day);
      }
    },
    [from, to],
  );

  const onDatesClear = React.useCallback(() => {
    onDateChange({
      from: undefined,
      to: undefined,
    });

    setEnteredTo(undefined);
    setFocused(FOCUSED_FIELD.FROM);
  }, [onDateChange]);

  const onOverlayExit = React.useCallback(() => {
    setEnteredTo(to);
    setFocused(null);
    if (onBlur) {
      onBlur();
    }
  }, [to, onBlur]);

  const onOverlayOpen = React.useCallback((field: FOCUSED_FIELD) => {
    setFocused(field);
  }, []);

  const onNoEndDate = React.useCallback(() => {
    onDateChange({
      from,
      to: undefined,
    });
    setFocused(null);
    setEnteredTo(undefined);
  }, [from, onDateChange]);

  // DatePickers props
  const firstAvailableDate = React.useMemo(() => {
    const localDate = new Date(
      firstAvailableDay(exclusions, listingFlexibleDays, minimumPeriod) ||
        today,
    );

    // to make sure unavailable date will not be selectable on the timezones behind the listing local time.
    return convertDateToUTC(localDate);
  }, [exclusions, listingFlexibleDays, minimumPeriod, today]);

  const firstAvailableToDate = () => {
    if (to) {
      return to;
    }

    if (from && minimumPeriod) {
      return calcFirstAvailableToDate(from, minimumPeriod, listingFlexibleDays);
    }

    return from;
  };

  const commonDatePickerProps = {
    numberOfMonths: !showSingleMonth ? 2 : 1,
    ...datePickerProps,
    mode: 'range',
    selected: from ? { from, to: enteredTo } : undefined,
  };
  const fromDatePickerProps = {
    ...commonDatePickerProps,
    onDayClick: onFromDateChange,
    onMonthChange: (changedMonth: Date) => {
      setFromDatePickerMonth(changedMonth);
    },
    month: fromDatePickerMonth || from || firstAvailableDate,
    modifiers: {
      disabled: isFromDisabled,
      start: from || false,
      end: enteredTo || false,
    },
  };

  const toDatePickerProps = {
    ...commonDatePickerProps,
    onDayClick: onToDateChange,
    onMonthChange: (changedMonth: Date) => {
      setToDatePickerMonth(changedMonth);
    },
    month: toDatePickerMonth || firstAvailableToDate(),
    modifiers: {
      start: from || false,
      end: enteredTo || false,
      disabled: isToDisabled,
    },
    onDayMouseEnter: onMouseEnter,
  };

  const finalDatePickerProps =
    focused === FOCUSED_FIELD.FROM ? fromDatePickerProps : toDatePickerProps;

  // DatePicker Inputs props
  const fromDateInputProps = {
    fullWidth: true,
    'data-test-locator': 'DateRangePicker / From',
    ...fromInputProps,
    label: fromInputLabel ?? T('Select'),
    required,
  };
  const toDateInputProps = {
    fullWidth: true,
    'data-test-locator': 'DateRangePicker / To',
    ...toInputProps,
    label: toInputLabel ?? T('Select'),
    required,
  };

  const Wrappers = {
    [DATEPICKER_VARIANT.INLINE]: DatePickerPopover,
    [DATEPICKER_VARIANT.DIALOG]: DatePickerDialog,
    [DATEPICKER_VARIANT.RESPONSIVE]: !showSingleMonth
      ? DatePickerPopover
      : DatePickerDialog,
  };

  return (
    <Grid container justifyContent="center">
      <DateRangePickerOverlay
        AdditionalInfo={AdditionalInfo}
        autoClose={autoClose}
        buttonProps={buttonProps}
        noEndDate={noEndDate}
        noEndDateLabel={noEndDateLabel ?? T('No end date')}
        CaptionSubtitle={CaptionSubtitle}
        DatePickerInputs={DatePickerInputs}
        focused={focused}
        fromInputProps={fromDateInputProps}
        toInputProps={toDateInputProps}
        fromEmptyValueLabel={fromEmptyValueLabel ?? T('Move-in date')}
        toEmptyValueLabel={toEmptyValueLabel ?? T('Move-out date')}
        datePickerProps={finalDatePickerProps as DayPickerProps}
        from={from}
        to={to}
        clearDatesButton={clearDatesButton}
        minimumPeriod={minimumPeriod}
        maximumPeriod={maximumStay}
        moveInWindows={moveInWindows}
        moveInWindowsTooltip={
          moveInWindowsTooltip ??
          T('date_picker.move_in_window.not_available_tip')
        }
        onDatesClear={onDatesClear}
        onNoEndDate={onNoEndDate}
        onOverlayExit={onOverlayExit}
        onOverlayOpen={onOverlayOpen}
        onOverlayDone={onOverlayDone}
        onOverlayCancel={onOverlayCancel}
        overlayDoneBtnLabel={overlayDoneBtnLabel ?? T('Done')}
        Overlay={Wrappers[variant]}
        header={header}
        T={T}
        formatDate={formatDate}
        withoutDividers={withoutDividers}
        dialogTitle={dialogTitle}
        fullScreenDialog={fullScreenDialog}
        showCancelButton={showCancelButton}
        localeString={localeString}
      />
    </Grid>
  );
};

export { DateRangePicker };
export default DateRangePicker;
